import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FormField, Select, TextInput } from '@percent/lemonade'
import type { VolunteeringPartner } from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'

export interface PartnerFieldProps {
  onChange: (value: string) => void
  touched?: boolean
  error?: string
  value?: string
  isLoading?: boolean
  availablePartners?: VolunteeringPartner[]
  readOnly?: boolean
}

export function PartnerField({
  onChange,
  touched,
  error,
  value,
  isLoading,
  availablePartners,
  readOnly = false
}: Readonly<PartnerFieldProps>) {
  const { t } = useTranslation()

  const options = useMemo(
    () =>
      (availablePartners ?? [])
        .map(partner => ({
          value: partner.id,
          label: partner.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availablePartners]
  )

  const selectedOption = value ? options.find(opt => opt.value === value) : undefined

  useEffect(() => {
    if (options.length === 1 && !readOnly) {
      onChange(options[0].value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, readOnly])

  const isSinglePartner = options.length === 1

  return (
    <FormField
      necessity="required"
      label={t('volunteering.opportunity.form.create.overviewSection.partnerField.label')}
      description={t('volunteering.opportunity.form.create.overviewSection.partnerField.helperText')}
      status={touched && error ? 'danger' : 'default'}
      statusMessage={error}
      data-testid="partnerField"
      hideCharacterCount
    >
      {readOnly || isSinglePartner ? (
        <TextInput name="partnerId" value={selectedOption?.label ?? ''} disabled readOnly />
      ) : (
        <Select
          options={options}
          placeholder={t('volunteering.opportunity.form.create.overviewSection.partnerField.placeholder')}
          searchable
          defaultValue={selectedOption}
          onChange={selectedValue => onChange(selectedValue.value)}
          isLoading={isLoading}
        />
      )}
    </FormField>
  )
}
